

import axios from "axios";

// Base URL - Centralized URL configuration
const API_URL = `${process.env.REACT_APP_API_URL}`;

// Initialize Axios instance for auth operations
export const adminApiService = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

export const adminService = {
  // 📌 Add new user (invite)
  async adduser(data) {
    try {
      const response = await adminApiService.post("/admin/add", data);

      if (response.status === 201) {
        return { success: true, message: "User invited successfully" };
      }

      return {
        success: false,
        message: response.data?.body?.message || "User add failed",
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.body?.message || "User add failed",
      };
    }
  },

  // 📌 Get all users (with pagination & filtering)
  async getuser(params = {}, config = {}) {
    try {
      const query = new URLSearchParams(
        Object.fromEntries(
          Object.entries(params).filter(([, value]) => value !== "" && value != null)
        )
      ).toString();
      const url = `/admin/get${query ? `?${query}` : ''}`;
      const response = await adminApiService.get(url, config);
      if (response.status === 200) {
        // data lives at response.data.body.data (contains { users, total, page, totalPages })
        return {
          success: true,
          message: "Users retrieved",
          data: response.data.body.data,
        };
      }
      return {
        success: false,
        message: response.data?.body?.message || "User retrieve failed",
        data: { users: [], total: 0, page: 1, totalPages: 0 },
      };
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        throw error;
      }
      return {
        success: false,
        message: error.response?.data?.body?.message || "User retrieve failed",
        data: { users: [], total: 0, page: 1, totalPages: 0 },
      };
    }
  },

  async getPlatformLimits() {
    try {
      const response = await adminApiService.get("/admin/platform-limits");
      if (response.status === 200) {
        const limits =
          response.data?.body?.data ?? response.data?.body ?? response.data;
        return { success: true, data: limits };
      }
      return {
        success: false,
        message: response.data?.body?.message || "Failed to load platform limits",
      };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.body?.message || "Failed to load platform limits",
      };
    }
  },

  async updatePlatformLimits(data) {
    try {
      const response = await adminApiService.put("/admin/platform-limits", data);
      if (response.status === 200) {
        const limits =
          response.data?.body?.data ?? response.data?.body ?? response.data;
        return {
          success: true,
          data: limits,
          message: response.data?.body?.message || "Platform limits updated",
        };
      }
      return {
        success: false,
        message: response.data?.body?.message || "Failed to update platform limits",
      };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.body?.message ||
          "Failed to update platform limits",
      };
    }
  },

  // 📌 Update user by ID
  async updateuser(userId, data) {
    try {
      const response = await adminApiService.put(`/admin/update/${userId}`, data);

      if (response.status === 200) {
        return { success: true, message: "User updated successfully" };
      }

      return {
        success: false,
        message: response.data?.body?.message || "User update failed",
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.body?.message || "User update failed",
      };
    }
  },

  // 📌 Delete user by ID
  async deleteuser(userId) {
    try {
      const response = await adminApiService.delete(`/admin/delete/${userId}`);

      if (response.status === 200) {
        return { success: true, message: "User deleted successfully" };
      }

      return {
        success: false,
        message: response.data?.body?.message || "User delete failed",
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.body?.message || "User delete failed",
      };
    }
  },

  // 📌 Global admin analytics dashboard
  async getGlobalDashboard(params = {}) {
    try {
      const query = new URLSearchParams();
      if (params.timeframe) query.set("timeframe", params.timeframe);
      if (params.fromDate) query.set("fromDate", params.fromDate);
      if (params.toDate) query.set("toDate", params.toDate);
      if (params.plan) query.set("plan", params.plan);
      if (params.region) query.set("region", params.region);

      const qs = query.toString();
      const response = await adminApiService.get(
        `/admin/global-dashboard${qs ? `?${qs}` : ""}`
      );

      if (response.status === 200) {
        return {
          success: true,
          data: response.data?.body?.data || response.data?.body || {},
        };
      }

      return {
        success: false,
        message:
          response.data?.body?.message || "Failed to load global dashboard",
        data: null,
      };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.body?.message ||
          "Failed to load global dashboard",
        data: null,
      };
    }
  },

  // 📌 Get Admin Dashboard Stats
  async getstats() {
    try {
      const response = await adminApiService.get("/admin/stats");

      if (response.status === 200) {
        return {
          success: true,
          message: "Stats retrieved",
          data: response.data.body.data,
        };
      }

      return {
        success: false,
        message: response.data?.body?.message || "Stats retrieve failed",
        data: {},
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.body?.message || "Stats retrieve failed",
        data: {},
      };
    }
  },

  // 📌 Reset password (Invitation Accept)
  async updatepassworduser(data) {
    try {
      const response = await adminApiService.post("/auth/reset-password", data);

      if (response.status === 200) {
        return { success: true, message: "Password updated successfully" };
      }

      return {
        success: false,
        message: response.data?.body?.message || "Password update failed",
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.body?.message || "Password update failed",
      };
    }
  },
 // 📌 Get Dashboard Statistics
  async getDashboardStatistics() {
    try {
      const response = await adminApiService.get("/admin/dashboard/statistics");
      
      if (response.status === 200) {
        return {
          success: true,
          message: "Dashboard statistics retrieved",
          data: response.data.body.data, // Changed from response.data.body to response.data.body.data
        };
      }
      
      return {
        success: false,
        message: response.data?.body?.message || "Failed to get dashboard statistics",
        data: {},
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.body?.message || "Failed to get dashboard statistics",
        data: {},
      };
    }
  },

  // 📌 Get Dashboard SOAP Notes with filters
  async getDashboardSOAPNotes(params = {}) {
    try {
      // Build query string from params
      const queryParams = new URLSearchParams();
      
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          queryParams.append(key, params[key]);
        }
      });

      const queryString = queryParams.toString();
      const url = `/admin/dashboard/soap-notes${queryString ? `?${queryString}` : ''}`;
      
      const response = await adminApiService.get(url);

      if (response.status === 200) {
        return {
          success: true,
          message: "SOAP notes retrieved",
          data: response.data.body.data, // Changed from response.data.body to response.data.body.data
        };
      }

      return {
        success: false,
        message: response.data?.body?.message || "Failed to get SOAP notes",
        data: { soapNotes: [], pagination: {} },
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.body?.message || "Failed to get SOAP notes",
        data: { soapNotes: [], pagination: {} },
      };
    }
  },

  // 📌 Fast user list for admin filter dropdowns (name + id only)
  async getFilterUsers() {
    try {
      const response = await adminApiService.get("/admin/dashboard/filter-users");
      if (response.status === 200) {
        return {
          success: true,
          message: "Filter users retrieved",
          data: response.data.body.data,
        };
      }
      return {
        success: false,
        message: response.data?.body?.message || "Failed to get filter users",
        data: { users: [] },
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.body?.message || "Failed to get filter users",
        data: { users: [] },
      };
    }
  },

  // 📌 Get users who created sessions or SOAP notes (Visits "Created by" filter)
  async getSessionCreators() {
    try {
      const response = await adminApiService.get("/admin/dashboard/session-creators");

      if (response.status === 200) {
        return {
          success: true,
          message: "Session creators retrieved",
          data: response.data.body.data,
        };
      }

      return {
        success: false,
        message: response.data?.body?.message || "Failed to get session creators",
        data: { users: [] },
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.body?.message || "Failed to get session creators",
        data: { users: [] },
      };
    }
  },

  // 📌 Get Assignable Users for dropdown
  async getAssignableUsers() {
    try {
      const response = await adminApiService.get("/admin/dashboard/assignable-users");

      if (response.status === 200) {
        return {
          success: true,
          message: "Assignable users retrieved",
          data: response.data.body.data, // Changed from response.data.body to response.data.body.data
        };
      }

      return {
        success: false,
        message: response.data?.body?.message || "Failed to get assignable users",
        data: { users: [] },
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.body?.message || "Failed to get assignable users",
        data: { users: [] },
      };
    }
  },

  // 📌 Get SOAP Note Feedback
  async getSOAPNoteFeedback(soapNoteId) {
    try {
      if (!soapNoteId) {
        return {
          success: false,
          message: "SOAP note ID is required",
          data: { feedbacks: [] },
        };
      }

      const response = await adminApiService.get(`/admin/soap-notes/${soapNoteId}/feedback`);

      if (response.status === 200) {
        return {
          success: true,
          message: "Feedback retrieved",
          data: response.data.body.data, // Changed from response.data.body to response.data.body.data
        };
      }

      return {
        success: false,
        message: response.data?.body?.message || "Failed to get feedback",
        data: { feedbacks: [] },
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.body?.message || "Failed to get feedback",
        data: { feedbacks: [] },
      };
    }
  },
  // 📌 Assign SOAP Note
  async assignSOAPNote(data) {
    try {
      const response = await adminApiService.post("/admin/soap-notes/assign", data);

      if (response.status === 200 || response.status === 201) {
        return {
          success: true,
          message: response.data?.message || "SOAP note assigned successfully",
          data: response.data.body,
        };
      }

      return {
        success: false,
        message: response.data?.body?.message || "Failed to assign SOAP note",
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.body?.message || "Failed to assign SOAP note",
      };
    }
  },

  // 📌 Reassign SOAP Note
  async reassignSOAPNote(data) {
    try {
      const response = await adminApiService.post("/admin/soap-notes/reassign", data);

      if (response.status === 200) {
        return {
          success: true,
          message: response.data?.message || "SOAP note reassigned successfully",
          data: response.data.body,
        };
      }

      return {
        success: false,
        message: response.data?.body?.message || "Failed to reassign SOAP note",
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.body?.message || "Failed to reassign SOAP note",
      };
    }
  },

  // 📌 Bulk Assign SOAP Notes
  async bulkAssignSOAPNotes(data) {
    try {
      const response = await adminApiService.post("/admin/soap-notes/bulk-assign", data);

      if (response.status === 200) {
        return {
          success: true,
          message: response.data?.message || "SOAP notes bulk assigned successfully",
          data: response.data.body,
        };
      }

      return {
        success: false,
        message: response.data?.body?.message || "Failed to bulk assign SOAP notes",
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.body?.message || "Failed to bulk assign SOAP notes",
      };
    }
  },

  // 📌 Get Assigned SOAP Notes (filtered by user)
  async getAssignedSOAPNotes(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          queryParams.append(key, params[key]);
        }
      });

      const queryString = queryParams.toString();
      const url = `/admin/soap-notes/assigned${queryString ? `?${queryString}` : ''}`;
      
      const response = await adminApiService.get(url);

      if (response.status === 200) {
        return {
          success: true,
          message: "Assigned SOAP notes retrieved",
          data: response.data.body,
        };
      }

      return {
        success: false,
        message: response.data?.body?.message || "Failed to get assigned SOAP notes",
        data: [],
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.body?.message || "Failed to get assigned SOAP notes",
        data: [],
      };
    }
  },

  // 📌 Get All Assigned SOAP Notes (admin view)
  async getAllAssignedSOAPNotes(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          queryParams.append(key, params[key]);
        }
      });

      const queryString = queryParams.toString();
      const url = `/admin/soap-notes/assigned/all${queryString ? `?${queryString}` : ''}`;
      
      const response = await adminApiService.get(url);

      if (response.status === 200) {
        return {
          success: true,
          message: "All assigned SOAP notes retrieved",
          data: response.data.body,
        };
      }

      return {
        success: false,
        message: response.data?.body?.message || "Failed to get all assigned SOAP notes",
        data: [],
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.body?.message || "Failed to get all assigned SOAP notes",
        data: [],
      };
    }
  },
  // 📌 Assign SOAP Note (single)
  async assignSOAPNote(data) {
    try {
      const response = await adminApiService.post("/admin/soap-notes/assign", data);
      
      if (response.status === 200 || response.status === 201) {
        return {
          success: true,
          message: response.data?.message || "SOAP note assigned successfully",
          data: response.data.body,
        };
      }
      
      return {
        success: false,
        message: response.data?.body?.message || "Failed to assign SOAP note",
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.body?.message || "Failed to assign SOAP note",
      };
    }
  },

  // 📌 Reassign SOAP Note
  async reassignSOAPNote(data) {
    try {
      const response = await adminApiService.post("/admin/soap-notes/reassign", data);
      
      if (response.status === 200) {
        return {
          success: true,
          message: response.data?.message || "SOAP note reassigned successfully",
          data: response.data.body,
        };
      }
      
      return {
        success: false,
        message: response.data?.body?.message || "Failed to reassign SOAP note",
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.body?.message || "Failed to reassign SOAP note",
      };
    }
  },

  // 📌 Bulk Assign SOAP Notes
  async bulkAssignSOAPNotes(data) {
    try {
      const response = await adminApiService.post("/admin/soap-notes/bulk-assign", data);
      
      if (response.status === 200) {
        return {
          success: true,
          message: response.data?.message || "SOAP notes bulk assigned successfully",
          data: response.data.body,
        };
      }
      
      return {
        success: false,
        message: response.data?.body?.message || "Failed to bulk assign SOAP notes",
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.body?.message || "Failed to bulk assign SOAP notes",
      };
    }
  },

  // 📌 Get Assigned SOAP Notes (admin view - filter by user)
  async getAssignedSOAPNotes(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          queryParams.append(key, params[key]);
        }
      });

      const queryString = queryParams.toString();
      const url = `/admin/soap-notes/assigned${queryString ? `?${queryString}` : ''}`;
      
      const response = await adminApiService.get(url);

      if (response.status === 200) {
        return {
          success: true,
          message: "Assigned SOAP notes retrieved",
          data: response.data.body,
        };
      }

      return {
        success: false,
        message: response.data?.body?.message || "Failed to get assigned SOAP notes",
        data: { soapNotes: [], pagination: {} },
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.body?.message || "Failed to get assigned SOAP notes",
        data: { soapNotes: [], pagination: {} },
      };
    }
  },

  // 📌 Get All Assigned SOAP Notes (admin/management view)
  async getAllAssignedSOAPNotes(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          queryParams.append(key, params[key]);
        }
      });

      const queryString = queryParams.toString();
      const url = `/admin/soap-notes/assigned/all${queryString ? `?${queryString}` : ''}`;
      
      const response = await adminApiService.get(url);

      if (response.status === 200) {
        return {
          success: true,
          message: "All assigned SOAP notes retrieved",
          data: response.data.body,
        };
      }

      return {
        success: false,
        message: response.data?.body?.message || "Failed to get all assigned SOAP notes",
        data: { soapNotes: [], pagination: {} },
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.body?.message || "Failed to get all assigned SOAP notes",
        data: { soapNotes: [], pagination: {} },
      };
    }
  },
// 📌 Get Feedback Audit Data
async getFeedbackAuditData(filters = {}) {
  try {
    const queryParams = new URLSearchParams();
    
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });

    const queryString = queryParams.toString();
    const url = `/admin/dashboard/feedback-audit${queryString ? `?${queryString}` : ''}`;
    
    console.log('Calling feedback audit endpoint:', url);
    
    const response = await adminApiService.get(url);

    if (response.status === 200) {
      return {
        success: true,
        message: "Feedback audit data retrieved",
        data: response.data?.body?.data || response.data?.data || response.data
      };
    }

    return {
      success: false,
      message: response.data?.body?.message || "Failed to get feedback audit data",
      data: { feedbacks: [], stats: {}, categories: [] },
    };
  } catch (error) {
    console.error('Error fetching feedback audit data:', error);
    return {
      success: false,
      message: error.response?.data?.body?.message || "Failed to get feedback audit data",
      data: { feedbacks: [], stats: {}, categories: [] },
    };
  }
},

// Fallback method if audit endpoint doesn't exist
async getFeedbackAuditFallbackData(filters = {}) {
  try {
    // Get all SOAP notes with feedback
    const soapNotesData = await this.getDashboardSOAPNotes({
      ...filters,
      limit: 100 // Get more records for audit
    });

    // Process to get feedback data
    const feedbackEntries = [];
    const categories = {};
    
    if (soapNotesData && soapNotesData.soapNotes) {
      soapNotesData.soapNotes.forEach(note => {
        if (note.feedbacks && note.feedbacks.length > 0) {
          note.feedbacks.forEach(feedback => {
            feedbackEntries.push({
              ...feedback,
              sessionId: note.sessionId,
              patientName: note.patient?.firstName 
                ? `${note.patient.firstName} ${note.patient.lastName}`.trim()
                : 'Unknown Patient',
              assignedTo: note.assignedUser?.profile
                ? `${note.assignedUser.profile.firstName} ${note.assignedUser.profile.lastName}`.trim()
                : note.assignedUser?.email || '-'
            });
          });
        }
      });
    }

    // Calculate stats
    const total = feedbackEntries.length;
    const positive = feedbackEntries.filter(f => f.type === 'thumbs_up').length;
    const negative = feedbackEntries.filter(f => f.type === 'thumbs_down').length;
    
    return {
      feedbackEntries,
      stats: {
        total,
        positive,
        negative,
        avgEditTime: '8.3m', // This would need actual calculation
        chartsReviewed: soapNotesData?.soapNotes?.filter(n => 
          n.status === 'approved' || n.status === 'signed'
        ).length || 0
      },
      categories: this.calculateFeedbackCategories(feedbackEntries)
    };
  } catch (error) {
    console.error('Error in fallback feedback audit:', error);
    throw error;
  }
},

// Calculate feedback categories
calculateFeedbackCategories(feedbacks) {
  const negativeFeedbacks = feedbacks.filter(f => f.type === 'thumbs_down');
  
  if (negativeFeedbacks.length === 0) {
    return [];
  }

  // Simple categorization based on explanation text
  const categories = {
    'Medical Accuracy': 0,
    'Completeness': 0,
    'Context Missing': 0,
    'Other Issues': 0
  };

  const medicalKeywords = ['accurate', 'correct', 'diagnosis', 'assessment', 'medical'];
  const completenessKeywords = ['complete', 'missing', 'incomplete', 'partial'];
  const contextKeywords = ['context', 'background', 'history', 'details'];

  negativeFeedbacks.forEach(feedback => {
    const explanation = feedback.explanation?.toLowerCase() || '';
    
    if (medicalKeywords.some(keyword => explanation.includes(keyword))) {
      categories['Medical Accuracy']++;
    } else if (completenessKeywords.some(keyword => explanation.includes(keyword))) {
      categories['Completeness']++;
    } else if (contextKeywords.some(keyword => explanation.includes(keyword))) {
      categories['Context Missing']++;
    } else {
      categories['Other Issues']++;
    }
  });

  // Convert to array format
  const total = negativeFeedbacks.length;
  return Object.entries(categories)
    .filter(([_, count]) => count > 0)
    .map(([name, count]) => ({
      name,
      count,
      percentage: Math.round((count / total) * 100)
    }));
},

async getMyAssignedSOAPNotes() {
  try {
    const response = await adminApiService.get("/admin/soap-notes/users/my-assigned");

    if (response.status === 200) {
      return {
        success: true,
        message: response.data.body.message || "My assigned SOAP notes retrieved",
        data: response.data.body.data,  // Changed: response.data.body.data instead of response.data.body
      };
    }

    return {
      success: false,
      message: response.data?.body?.message || "Failed to get my assigned SOAP notes",
      data: [],
    };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.body?.message || "Failed to get my assigned SOAP notes",
      data: [],
    };
  }
}
};